import styled from 'styled-components';

import { media } from 'src/styles/breakpoints';

/* The toolbar primitives now live with the other shared components so every
   list page shares one baseline; re-exported here for this page's own imports. */
export {
  ToolButton,
  ToolbarRow,
  ToolbarFilters,
  DateRangeGroup,
  FieldControl,
  SearchControl,
  FilterControl,
  DateControl,
  FieldLabel,
  DateLabel,
  DateInput,
  ToggleButton,
  ToolbarActions,
  ToolbarActionButtons,
} from 'src/components/Toolbar';

export const LogTabs = styled.nav`
  display: flex;
  flex-wrap: nowrap;
  gap: 1.75rem;
  margin: 0 0 1.25rem;
  border-bottom: 1px solid ${({ theme }) => theme.grey};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const LogTab = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  position: relative;
  padding: 0.75rem 0;
  border: none;
  background: transparent;
  color: ${({ theme, $active }) => ($active ? theme.dark : theme.midGrey)};
  font-size: 1rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: ${({ theme, $active }) => ($active ? theme.dark : 'transparent')};
  }

  &:hover {
    color: ${({ theme }) => theme.dark};
  }
`;

export const SpinIcon = styled.span<{ $spin?: boolean }>`
  display: inline-flex;
  animation: ${({ $spin }) => ($spin ? 'logs-spin 0.8s linear infinite' : 'none')};

  @keyframes logs-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;

  ${media.md} {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled.div`
  padding: 1rem 1.1rem;
  border-radius: 0.75rem;
  background: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.grey};
`;

export const StatLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.midGrey};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 0.35rem;
`;

export const StatValue = styled.div`
  font-size: 1.35rem;
  font-weight: 700;
  color: ${({ theme }) => theme.dark};
`;

export const ProviderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(11.5rem, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

export const ProviderCard = styled.button<{ $active: boolean; $hasFailures: boolean }>`
  text-align: left;
  padding: 0.85rem 0.95rem;
  border-radius: 0.75rem;
  border: 1px solid
    ${({ theme, $active, $hasFailures }) => {
      if ($active) return theme.primary;
      if ($hasFailures) return theme.negative;
      return theme.grey;
    }};
  background: ${({ theme, $active }) => ($active ? theme.primary90 : theme.background)};
  color: ${({ theme }) => theme.dark};
  cursor: pointer;
  font-family: inherit;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ theme, $active }) => ($active ? theme.primary : theme.midGrey)};
    box-shadow: 0 4px 12px ${({ theme }) => theme.light20};
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.lightGrey};
  }
`;

export const ProviderName = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 0.35rem;
`;

export const ProviderMeta = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.midGrey};
  line-height: 1.45;
`;

export const ActorCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

export const ActorEmail = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TypeBadge = styled.span`
  display: inline-flex;
  align-self: flex-start;
  padding: 0.15rem 0.45rem;
  border-radius: 0.35rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: capitalize;
  color: ${({ theme }) => theme.dark};
  background: ${({ theme }) => theme.primary90};
`;

export const StatusCode = styled.span<{ $tone: 'ok' | 'client' | 'server' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 0.4rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme, $tone }) => {
    if ($tone === 'ok') return theme.positive;
    if ($tone === 'client') return theme.middle;
    if ($tone === 'server') return theme.negative;
    return theme.dark;
  }};
  background: ${({ theme, $tone }) => {
    if ($tone === 'ok') return theme.positive90;
    if ($tone === 'client') return theme.middle90;
    if ($tone === 'server') return theme.negative90;
    return theme.lightGrey;
  }};
`;

export const DurationText = styled.span<{ $tone: 'fast' | 'slow' | 'critical' | 'neutral' }>`
  font-weight: 600;
  color: ${({ theme, $tone }) => {
    if ($tone === 'fast') return theme.positive;
    if ($tone === 'slow') return theme.middle;
    if ($tone === 'critical') return theme.negative;
    return theme.dark;
  }};
`;

export const OkBadge = styled.span<{ $ok: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme, $ok }) => ($ok ? theme.positive : theme.negative)};
  background: ${({ theme, $ok }) => ($ok ? theme.positive90 : theme.negative90)};
`;

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.25rem;

  ${media.md} {
    grid-template-columns: 1fr;
  }
`;

export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`;

export const DetailLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.midGrey};
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

export const DetailValue = styled.div`
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.dark};
  word-break: break-word;
`;

export const MetaBlock = styled.pre`
  margin: 0;
  padding: 0.85rem 1rem;
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.lightGrey};
  border: 1px solid ${({ theme }) => theme.grey};
  font-size: 0.8125rem;
  line-height: 1.45;
  overflow: auto;
  max-height: 18rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const ModalTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${({ theme }) => theme.dark};
  margin: 0.25rem 0 1rem;
`;

export const DetailSection = styled.div`
  margin-top: 1.25rem;
`;

export const RestrictedNote = styled.div`
  padding: 2rem 0;
  color: ${({ theme }) => theme.midGrey};
  font-size: 0.95rem;
`;
